import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Brain, Bot, MessageSquare, Zap, GraduationCap, BookOpen,
  Wrench, Shield, BarChart3, Settings, Activity, Eye, Play,
  Pause, RefreshCw, Plus, Search, Filter, ChevronRight,
  Clock, CheckCircle2, AlertTriangle, XCircle, TrendingUp,
  TrendingDown, Users, Calendar, FileText, Database, Lock,
  Unlock, ThumbsUp, ThumbsDown, Flag, ExternalLink, Send,
  Loader2, MoreVertical, Edit, Trash2, Copy, Download,
  Upload, Star, Target, Lightbulb, Sparkles, CircuitBoard,
  Cpu, Network, Layers, Globe, Phone, Mail, Hash, ArrowRight,
  AlertCircle, Info, HelpCircle, BookMarked, Workflow, Crown,
  Image, Save, RotateCcw, Pin, History, ChevronLeft, ChevronDown,
  Sliders, TestTube, MessageCircle, Bug
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tab configurations - Updated with Prime tab
const TABS = [
  { id: 'dashboard', label: 'AI Dashboard', icon: Activity },
  { id: 'prime', label: 'Prime', icon: Crown },
  { id: 'assistant', label: 'AI Assistant', icon: Bot },
  { id: 'chatbots', label: 'Chatbots', icon: MessageSquare },
  { id: 'agents', label: 'AI Agents', icon: Zap },
  { id: 'trainer', label: 'AI Trainer', icon: GraduationCap },
  { id: 'knowledge', label: 'Knowledge Base', icon: BookOpen },
  { id: 'tools', label: 'Automation & Tools', icon: Wrench },
  { id: 'governance', label: 'Safety & Governance', icon: Shield },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'settings', label: 'Global Settings', icon: Settings },
];

// ============== DASHBOARD TAB ==============
const DashboardTab = () => {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filters, setFilters] = useState({ ai_system: '', status: '', days: 7 });

  useEffect(() => {
    fetchDashboard();
  }, [filters.days]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [statsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/platform-ai/dashboard/stats?days=${filters.days}`),
        axios.get(`${API}/platform-ai/dashboard/activity?days=${filters.days}&limit=50`)
      ]);
      setStats(statsRes.data);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'needs_review': return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'escalated': return <Flag className="h-4 w-4 text-orange-500" />;
      default: return <Clock className="h-4 w-4 text-slate-500" />;
    }
  };

  const getSystemIcon = (system) => {
    switch (system) {
      case 'assistant': return <Bot className="h-4 w-4" />;
      case 'chatbot': return <MessageSquare className="h-4 w-4" />;
      case 'agent': return <Zap className="h-4 w-4" />;
      case 'trainer': return <GraduationCap className="h-4 w-4" />;
      default: return <Brain className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Activity className="h-8 w-8 text-blue-500" />
              <Badge variant="outline">{filters.days}d</Badge>
            </div>
            <p className="text-2xl font-bold mt-2">{stats?.total_events || 0}</p>
            <p className="text-sm text-muted-foreground">Total AI Events</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
              <TrendingUp className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{stats?.resolution_rate || 0}%</p>
            <p className="text-sm text-muted-foreground">Resolution Rate</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Flag className="h-8 w-8 text-orange-500" />
              <span className="text-xs text-muted-foreground">{stats?.by_status?.escalated || 0}</span>
            </div>
            <p className="text-2xl font-bold mt-2">{stats?.escalation_rate || 0}%</p>
            <p className="text-sm text-muted-foreground">Escalation Rate</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{stats?.needs_review || 0}</p>
            <p className="text-sm text-muted-foreground">Needs Review</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Hash className="h-8 w-8 text-purple-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{(stats?.cost?.total_tokens || 0).toLocaleString()}</p>
            <p className="text-sm text-muted-foreground">Tokens Used</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <TrendingUp className="h-8 w-8 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold mt-2">${stats?.cost?.total_dollars || 0}</p>
            <p className="text-sm text-muted-foreground">Total Cost</p>
          </CardContent>
        </Card>
      </div>

      {/* System Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {['assistant', 'chatbot', 'agent', 'trainer'].map(system => (
          <Card key={system}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`p-3 rounded-lg ${
                system === 'assistant' ? 'bg-blue-100 dark:bg-blue-900' :
                system === 'chatbot' ? 'bg-green-100 dark:bg-green-900' :
                system === 'agent' ? 'bg-purple-100 dark:bg-purple-900' :
                'bg-orange-100 dark:bg-orange-900'
              }`}>
                {getSystemIcon(system)}
              </div>
              <div>
                <p className="text-xl font-bold">{stats?.by_system?.[system] || 0}</p>
                <p className="text-sm text-muted-foreground capitalize">{system}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Activity Feed */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Activity Feed
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={filters.days.toString()} onValueChange={(v) => setFilters({...filters, days: parseInt(v)})}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchDashboard}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {events.map((event) => (
                <div
                  key={event.id}
                  onClick={() => setSelectedEvent(event)}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent cursor-pointer border"
                >
                  {getSystemIcon(event.ai_system)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium capitalize">{event.ai_system}</span>
                      <Badge variant="outline" className="text-xs">{event.event_type}</Badge>
                      {event.user_email && (
                        <span className="text-xs text-muted-foreground">{event.user_email}</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {event.prompt || event.response || 'No content'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(event.status)}
                    <span className="text-xs text-muted-foreground">
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))}
              {events.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Activity className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No AI events recorded yet</p>
                  <Button variant="link" onClick={() => axios.post(`${API}/platform-ai/seed-demo-data`).then(() => { toast.success('Demo data seeded!'); fetchDashboard(); })}>
                    Seed demo data
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Event Detail Sheet */}
      <Sheet open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <SheetContent className="w-[500px] sm:w-[600px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {selectedEvent && getSystemIcon(selectedEvent.ai_system)}
              Event Replay
            </SheetTitle>
            <SheetDescription>
              {selectedEvent?.timestamp && new Date(selectedEvent.timestamp).toLocaleString()}
            </SheetDescription>
          </SheetHeader>
          {selectedEvent && (
            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-2">
                {getStatusIcon(selectedEvent.status)}
                <Badge>{selectedEvent.status}</Badge>
                <Badge variant="outline">{selectedEvent.event_type}</Badge>
              </div>

              {selectedEvent.prompt && (
                <div>
                  <Label className="text-xs text-muted-foreground">USER PROMPT</Label>
                  <div className="mt-1 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    <p className="text-sm">{selectedEvent.prompt}</p>
                  </div>
                </div>
              )}

              {selectedEvent.response && (
                <div>
                  <Label className="text-xs text-muted-foreground">AI RESPONSE</Label>
                  <div className="mt-1 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm">{selectedEvent.response}</p>
                  </div>
                </div>
              )}

              <Separator />

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">TOKENS USED</Label>
                  <p className="font-medium">{selectedEvent.tokens_used}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">COST</Label>
                  <p className="font-medium">${(selectedEvent.cost_cents / 100).toFixed(4)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">RESPONSE TIME</Label>
                  <p className="font-medium">{selectedEvent.response_time_ms}ms</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">MODEL</Label>
                  <p className="font-medium">{selectedEvent.model_used}</p>
                </div>
              </div>

              {selectedEvent.knowledge_sources?.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">KNOWLEDGE SOURCES</Label>
                  <div className="mt-1 space-y-1">
                    {selectedEvent.knowledge_sources.map((src, i) => (
                      <Badge key={i} variant="secondary">{src.doc_id || src.collection}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedEvent.tools_called?.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">TOOLS CALLED</Label>
                  <div className="mt-1 space-y-1">
                    {selectedEvent.tools_called.map((tool, i) => (
                      <Badge key={i} variant="outline">{tool.tool_name}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

// ============== ASSISTANT TAB ==============
const AssistantTab = () => {
  const [policies, setPolicies] = useState([]);
  const [settings, setSettings] = useState({ assistant_enabled: true });

  const fetchPolicies = useCallback(async () => {
    try {
      const [policiesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/platform-ai/assistant/policies`),
        axios.get(`${API}/platform-ai/settings`)
      ]);
      setPolicies(policiesRes.data.policies || []);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching policies:', error);
    }
  }, []);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const toggleAssistant = async (enabled) => {
    try {
      await axios.patch(`${API}/platform-ai/settings`, { assistant_enabled: enabled });
      setSettings({ ...settings, assistant_enabled: enabled });
      toast.success(`Assistant ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  return (
    <div className="space-y-6">
      {/* Global Toggle */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-xl">
                <Bot className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">AI Assistant</h3>
                <p className="text-sm text-muted-foreground">
                  In-app assistant available to all employees via header button
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={settings.assistant_enabled ? "default" : "secondary"}>
                {settings.assistant_enabled ? "Active" : "Disabled"}
              </Badge>
              <Switch
                checked={settings.assistant_enabled}
                onCheckedChange={toggleAssistant}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Capabilities Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Read-Only Access
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Can view jobs, customers, appointments, and answer questions.
            </p>
            <div className="mt-3 flex items-center justify-between">
              <Badge variant="secondary">All Roles</Badge>
              <Switch defaultChecked />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Edit className="h-4 w-4" />
              Draft Capabilities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Can draft messages and notes for user review.
            </p>
            <div className="mt-3 flex items-center justify-between">
              <Badge variant="secondary">Sales, Managers</Badge>
              <Switch defaultChecked />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Execute Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Can create tasks and send messages with approval.
            </p>
            <div className="mt-3 flex items-center justify-between">
              <Badge variant="secondary">Managers Only</Badge>
              <Switch />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Department Modes */}
      <Card>
        <CardHeader>
          <CardTitle>Department Modes</CardTitle>
          <CardDescription>
            The assistant automatically adapts behavior based on user&apos;s department
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['Sales', 'Operations', 'Call Center', 'Marketing'].map(dept => (
              <div key={dept} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{dept}</span>
                  <Switch defaultChecked />
                </div>
                <p className="text-xs text-muted-foreground">
                  {dept === 'Sales' && 'Scripts, pricing, objection handling'}
                  {dept === 'Operations' && 'Scheduling, crew management, procedures'}
                  {dept === 'Call Center' && 'FAQs, troubleshooting, escalation'}
                  {dept === 'Marketing' && 'Campaign info, brand guidelines'}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Escalation Rules */}
      <Card>
        <CardHeader>
          <CardTitle>Escalation Rules</CardTitle>
          <CardDescription>
            When the assistant should hand off to a human
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <p className="font-medium">Keyword Triggers</p>
              <p className="text-sm text-muted-foreground">
                &quot;urgent&quot;, &quot;emergency&quot;, &quot;manager&quot;, &quot;supervisor&quot;, &quot;complaint&quot;
              </p>
            </div>
            <Switch defaultChecked />
          </div>
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <p className="font-medium">Confidence Threshold</p>
              <p className="text-sm text-muted-foreground">
                Escalate when AI confidence is below 60%
              </p>
            </div>
            <Switch defaultChecked />
          </div>
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <p className="font-medium">Repeated Questions</p>
              <p className="text-sm text-muted-foreground">
                Escalate after 3 similar questions
              </p>
            </div>
            <Switch />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== CHATBOTS TAB ==============
const ChatbotsTab = () => {
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChatbots();
  }, []);

  const fetchChatbots = async () => {
    try {
      const res = await axios.get(`${API}/platform-ai/chatbots`);
      setChatbots(res.data.chatbots || []);
    } catch (error) {
      console.error('Error fetching chatbots:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'website': return <Globe className="h-4 w-4" />;
      case 'sms': return <Phone className="h-4 w-4" />;
      case 'facebook': return <MessageSquare className="h-4 w-4" />;
      case 'email': return <Mail className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Chatbots</h3>
          <p className="text-sm text-muted-foreground">
            Create and manage chatbots for different channels
          </p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Create Chatbot
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {chatbots.map(bot => (
          <Card key={bot.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    bot.purpose === 'lead_capture' ? 'bg-green-100 dark:bg-green-900' :
                    bot.purpose === 'support' ? 'bg-blue-100 dark:bg-blue-900' :
                    'bg-purple-100 dark:bg-purple-900'
                  }`}>
                    <MessageSquare className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{bot.name}</CardTitle>
                    <p className="text-xs text-muted-foreground capitalize">{bot.purpose?.replace('_', ' ')}</p>
                  </div>
                </div>
                <Switch checked={bot.enabled} />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">{bot.description}</p>
              
              <div className="flex items-center gap-2 mb-3">
                {bot.channels?.map(channel => (
                  <Badge key={channel} variant="outline" className="gap-1">
                    {getChannelIcon(channel)}
                    {channel}
                  </Badge>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">
                  <p className="text-muted-foreground text-xs">Conversations</p>
                  <p className="font-medium">{bot.total_conversations}</p>
                </div>
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">
                  <p className="text-muted-foreground text-xs">Resolution</p>
                  <p className="font-medium">{bot.resolution_rate}%</p>
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1">
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button variant="ghost" size="sm">
                  <BarChart3 className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {chatbots.length === 0 && !loading && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-muted-foreground mb-3">No chatbots created yet</p>
              <Button variant="outline" onClick={() => axios.post(`${API}/platform-ai/seed-demo-data`).then(() => { toast.success('Demo data seeded!'); fetchChatbots(); })}>
                Seed demo data
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ============== AGENTS TAB ==============
const AgentsTab = () => {
  const [agents, setAgents] = useState([]);
  const [counts, setCounts] = useState({ total: 0, live: 0, test: 0, pending_approval: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [filter, setFilter] = useState('all');
  const [pendingApprovals, setPendingApprovals] = useState({ pending_agents: [], pending_runs: [] });

  useEffect(() => {
    fetchAgents();
    fetchPendingApprovals();
  }, [filter]);

  const fetchAgents = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/platform-ai/agents${params}`);
      setAgents(res.data.agents || []);
      setCounts(res.data.counts || { total: 0, live: 0, test: 0, pending_approval: 0 });
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const res = await axios.get(`${API}/platform-ai/agents/queue/pending`);
      setPendingApprovals(res.data);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    }
  };

  const runAgent = async (agentId) => {
    try {
      await axios.post(`${API}/platform-ai/agents/${agentId}/run`);
      toast.success('Agent triggered successfully');
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run agent');
    }
  };

  const updateAgentStatus = async (agentId, newStatus) => {
    try {
      await axios.post(`${API}/platform-ai/agents/${agentId}/status`, null, {
        params: { new_status: newStatus }
      });
      toast.success(`Agent status updated to ${newStatus}`);
      fetchAgents();
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const toggleAgentEnabled = async (agentId, currentEnabled) => {
    try {
      await axios.patch(`${API}/platform-ai/agents/${agentId}`, { enabled: !currentEnabled });
      toast.success(currentEnabled ? 'Agent disabled' : 'Agent enabled');
      fetchAgents();
    } catch (error) {
      toast.error('Failed to toggle agent');
    }
  };

  const getTriggerIcon = (type) => {
    switch (type) {
      case 'schedule': return <Clock className="h-4 w-4" />;
      case 'event': return <Zap className="h-4 w-4" />;
      case 'manual': return <Play className="h-4 w-4" />;
      default: return <Workflow className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status, mode) => {
    const config = {
      live: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Live' },
      test: { color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', label: 'Test' },
      pending_approval: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'Pending' },
      paused: { color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400', label: 'Paused' },
      draft: { color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400', label: 'Draft' },
      archived: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Archived' },
    };
    const cfg = config[status] || config.draft;
    return <Badge className={`${cfg.color} text-xs`}>{cfg.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">AI Agents</h3>
          <p className="text-sm text-muted-foreground">
            Automated workflows powered by AI • {counts.total} agents
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="h-4 w-4 mr-2" />
          Create Agent
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:border-blue-300 transition-colors" onClick={() => setFilter('all')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{counts.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-green-300 transition-colors" onClick={() => setFilter('live')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{counts.live}</p>
            <p className="text-xs text-muted-foreground">Live</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-amber-300 transition-colors" onClick={() => setFilter('test')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{counts.test}</p>
            <p className="text-xs text-muted-foreground">Test</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-blue-300 transition-colors" onClick={() => setFilter('pending_approval')}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{counts.pending_approval}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Approvals Queue */}
      {pendingApprovals.total_pending > 0 && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Pending Approvals ({pendingApprovals.total_pending})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingApprovals.pending_agents.map(agent => (
                <div key={agent.id} className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Zap className="h-4 w-4 text-purple-500" />
                    <div>
                      <p className="font-medium text-sm">{agent.name}</p>
                      <p className="text-xs text-muted-foreground">Requesting Live deployment</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => updateAgentStatus(agent.id, 'test')}>
                      Test First
                    </Button>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => updateAgentStatus(agent.id, 'live')}>
                      Approve Live
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {['all', 'live', 'test', 'pending_approval', 'paused'].map(f => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            className="capitalize"
          >
            {f === 'pending_approval' ? 'Pending' : f}
          </Button>
        ))}
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {agents.map(agent => (
          <Card key={agent.id} className={!agent.enabled ? 'opacity-60' : ''}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <Zap className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{agent.name}</CardTitle>
                    <div className="flex items-center gap-2 mt-1">
                      {getStatusBadge(agent.status, agent.mode)}
                      <Badge variant="outline" className="text-xs gap-1">
                        {getTriggerIcon(agent.trigger_type)}
                        {agent.trigger_type}
                      </Badge>
                      {agent.requires_approval && (
                        <Badge variant="secondary" className="text-xs">
                          <Lock className="h-3 w-3 mr-1" />
                          Approval
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <Switch
                  checked={agent.enabled}
                  onCheckedChange={() => toggleAgentEnabled(agent.id, agent.enabled)}
                />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">{agent.purpose}</p>
              
              {/* Capabilities Preview */}
              {agent.capabilities && agent.capabilities.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground mb-1">Capabilities</p>
                  <div className="flex flex-wrap gap-1">
                    {agent.capabilities.slice(0, 4).map(cap => (
                      <Badge key={cap} variant="outline" className="text-xs">
                        {cap.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                    {agent.capabilities.length > 4 && (
                      <Badge variant="outline" className="text-xs">
                        +{agent.capabilities.length - 4}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded text-center">
                  <p className="font-medium">{agent.total_runs || 0}</p>
                  <p className="text-muted-foreground text-xs">Runs</p>
                </div>
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded text-center">
                  <p className="font-medium">{agent.success_rate || 0}%</p>
                  <p className="text-muted-foreground text-xs">Success</p>
                </div>
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded text-center">
                  <p className="font-medium text-xs">
                    {agent.last_run_at ? new Date(agent.last_run_at).toLocaleDateString() : 'Never'}
                  </p>
                  <p className="text-muted-foreground text-xs">Last Run</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => runAgent(agent.id)}
                  disabled={!agent.enabled}
                >
                  <Play className="h-3 w-3 mr-1" />
                  Run Now
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setEditingAgent(agent); setShowCreateModal(true); }}>
                  <Edit className="h-3 w-3" />
                </Button>
                {agent.status === 'test' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-green-600"
                    onClick={() => updateAgentStatus(agent.id, 'live')}
                  >
                    Go Live
                  </Button>
                )}
                {agent.status === 'live' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-amber-600"
                    onClick={() => updateAgentStatus(agent.id, 'paused')}
                  >
                    Pause
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {agents.length === 0 && !loading && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <Zap className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-muted-foreground mb-3">
                {filter === 'all' ? 'No agents created yet' : `No ${filter} agents`}
              </p>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Agent
                </Button>
                <Button variant="outline" onClick={() => axios.post(`${API}/platform-ai/seed-demo-data`).then(() => { toast.success('Demo data seeded!'); fetchAgents(); })}>
                  Seed Demo Data
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Modal - Using dynamic import pattern */}
      {showCreateModal && (
        <AgentFactoryModalWrapper
          open={showCreateModal}
          onClose={() => { setShowCreateModal(false); setEditingAgent(null); }}
          onSuccess={() => { fetchAgents(); fetchPendingApprovals(); }}
          editAgent={editingAgent}
        />
      )}
    </div>
  );
};

// Wrapper component to dynamically load the modal
const AgentFactoryModalWrapper = ({ open, onClose, onSuccess, editAgent }) => {
  const [AgentFactoryModal, setAgentFactoryModal] = useState(null);

  useEffect(() => {
    import('../components/AgentFactoryModal').then(module => {
      setAgentFactoryModal(() => module.default);
    });
  }, []);

  if (!AgentFactoryModal) return null;

  return <AgentFactoryModal open={open} onClose={onClose} onSuccess={onSuccess} editAgent={editAgent} />;
};

// ============== TRAINER TAB ==============
const TrainerTab = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTracks();
  }, []);

  const fetchTracks = async () => {
    try {
      const res = await axios.get(`${API}/platform-ai/trainer/tracks`);
      setTracks(res.data.tracks || []);
    } catch (error) {
      console.error('Error fetching tracks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'onboarding': return 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400';
      case 'sales': return 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400';
      case 'operations': return 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400';
      case 'call_center': return 'bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400';
      default: return 'bg-slate-100 dark:bg-slate-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">AI Training Hub</h3>
          <p className="text-sm text-muted-foreground">
            AI-powered onboarding and ongoing training
          </p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Create Track
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tracks.map(track => (
          <Card key={track.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getTypeColor(track.type)}`}>
                  <GraduationCap className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-base">{track.name}</CardTitle>
                  <Badge variant="outline" className="text-xs capitalize mt-1">
                    {track.type?.replace('_', ' ')}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">{track.description}</p>
              
              <div className="space-y-2 mb-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Completion Rate</span>
                  <span className="font-medium">{track.completion_rate}%</span>
                </div>
                <Progress value={track.completion_rate} className="h-2" />
              </div>

              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded text-center">
                  <p className="font-medium">{track.total_enrolled}</p>
                  <p className="text-muted-foreground text-xs">Enrolled</p>
                </div>
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded text-center">
                  <p className="font-medium">{track.estimated_duration_minutes}m</p>
                  <p className="text-muted-foreground text-xs">Duration</p>
                </div>
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded text-center">
                  <p className="font-medium">{track.avg_score}%</p>
                  <p className="text-muted-foreground text-xs">Avg Score</p>
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1">
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button variant="ghost" size="sm">
                  <Users className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {tracks.length === 0 && !loading && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <GraduationCap className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-muted-foreground mb-3">No training tracks created yet</p>
              <Button variant="outline" onClick={() => axios.post(`${API}/platform-ai/seed-demo-data`).then(() => { toast.success('Demo data seeded!'); fetchTracks(); })}>
                Seed demo data
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ============== KNOWLEDGE BASE TAB ==============
const KnowledgeBaseTab = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const res = await axios.get(`${API}/platform-ai/knowledge-base/collections`);
      setCollections(res.data.collections || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Knowledge Base</h3>
          <p className="text-sm text-muted-foreground">
            Documents and data that power AI responses
          </p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New Collection
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {collections.map(collection => (
          <Card key={collection.id} className="hover:shadow-md transition-shadow cursor-pointer">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                  <BookOpen className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <CardTitle className="text-base">{collection.name}</CardTitle>
                  <div className="flex items-center gap-2 mt-1">
                    {collection.visibility !== 'all' && (
                      <Badge variant="secondary" className="text-xs">
                        <Lock className="h-3 w-3 mr-1" />
                        Restricted
                      </Badge>
                    )}
                    {collection.require_citation && (
                      <Badge variant="outline" className="text-xs">Citations</Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">{collection.description}</p>
              
              <div className="flex items-center justify-between p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{collection.document_count} documents</span>
                </div>
                <Button variant="ghost" size="sm">
                  <Upload className="h-4 w-4" />
                </Button>
              </div>

              {collection.last_updated && (
                <p className="text-xs text-muted-foreground mt-2">
                  Updated {new Date(collection.last_updated).toLocaleDateString()}
                </p>
              )}
            </CardContent>
          </Card>
        ))}

        {collections.length === 0 && !loading && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-muted-foreground mb-3">No knowledge base collections yet</p>
              <Button variant="outline" onClick={() => axios.post(`${API}/platform-ai/seed-demo-data`).then(() => { toast.success('Demo data seeded!'); fetchCollections(); })}>
                Seed demo data
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ============== TOOLS TAB ==============
const ToolsTab = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      const res = await axios.get(`${API}/platform-ai/tools`);
      setTools(res.data.tools || []);
    } catch (error) {
      console.error('Error fetching tools:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Automation & Tools</h3>
          <p className="text-sm text-muted-foreground">
            APIs and tools available to AI systems
          </p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Register Tool
        </Button>
      </div>

      <div className="space-y-3">
        {tools.map(tool => (
          <Card key={tool.id}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    <Wrench className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-medium">{tool.name}</h4>
                    <p className="text-sm text-muted-foreground">{tool.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline">{tool.category}</Badge>
                  {tool.requires_approval && (
                    <Badge variant="secondary">
                      <Lock className="h-3 w-3 mr-1" />
                      Approval
                    </Badge>
                  )}
                  <Switch checked={tool.enabled} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {tools.length === 0 && !loading && (
          <Card>
            <CardContent className="py-12 text-center">
              <Wrench className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-muted-foreground mb-3">No tools registered yet</p>
              <Button variant="outline" onClick={() => axios.post(`${API}/platform-ai/seed-demo-data`).then(() => { toast.success('Demo data seeded!'); fetchTools(); })}>
                Seed demo data
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ============== GOVERNANCE TAB ==============
const GovernanceTab = () => {
  const [approvals, setApprovals] = useState([]);

  const fetchApprovals = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform-ai/governance/approvals`);
      setApprovals(res.data.approvals || []);
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Safety & Governance</h3>
          <p className="text-sm text-muted-foreground">
            Guardrails, policies, and approval workflows
          </p>
        </div>
      </div>

      {/* Policies */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4" />
              PII Protection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Automatically detect and redact sensitive personal information
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Detect SSN</span>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Detect Credit Cards</span>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Redact in Logs</span>
                <Switch defaultChecked />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Content Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Block inappropriate or off-topic content
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Block Profanity</span>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Block Competitor Mentions</span>
                <Switch />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Off-topic Detection</span>
                <Switch defaultChecked />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Action Approvals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Require approval for high-risk AI actions
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Send External Messages</span>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Create/Update Records</span>
                <Switch />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Access Financials</span>
                <Switch defaultChecked />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4" />
              Data Retention
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Configure how long AI data is stored
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-sm">Event Logs</Label>
                <Select defaultValue="90">
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Conversations</Label>
                <Select defaultValue="365">
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                    <SelectItem value="730">2 years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Approvals */}
      {approvals.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Approvals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {approvals.map(approval => (
                <div key={approval.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{approval.action_description}</p>
                    <p className="text-sm text-muted-foreground">
                      {approval.ai_system} • {new Date(approval.requested_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline">Reject</Button>
                    <Button size="sm">Approve</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== ANALYTICS TAB ==============
const AnalyticsTab = () => {
  const [analytics, setAnalytics] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform-ai/analytics/overview?days=30`);
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Analytics & Evaluations</h3>
          <p className="text-sm text-muted-foreground">
            AI performance metrics and feedback analysis
          </p>
        </div>
        <Select defaultValue="30">
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Feedback Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Star className="h-8 w-8 text-amber-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {analytics?.feedback?.avg_rating?.toFixed(1) || '0.0'}
            </p>
            <p className="text-sm text-muted-foreground">Avg Rating</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <ThumbsUp className="h-8 w-8 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{analytics?.feedback?.positive || 0}</p>
            <p className="text-sm text-muted-foreground">Positive</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <ThumbsDown className="h-8 w-8 text-red-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{analytics?.feedback?.negative || 0}</p>
            <p className="text-sm text-muted-foreground">Negative</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <HelpCircle className="h-8 w-8 text-orange-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{analytics?.knowledge_gaps || 0}</p>
            <p className="text-sm text-muted-foreground">Knowledge Gaps</p>
          </CardContent>
        </Card>
      </div>

      {/* Cost by Department */}
      <Card>
        <CardHeader>
          <CardTitle>Cost by Department</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {analytics?.cost_by_department?.map((dept, i) => (
              <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="capitalize">{dept._id || 'Unknown'}</span>
                </div>
                <div className="text-right">
                  <p className="font-medium">${(dept.total_cost / 100).toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">
                    {dept.total_requests} requests • {dept.total_tokens?.toLocaleString()} tokens
                  </p>
                </div>
              </div>
            )) || (
              <div className="text-center py-8 text-muted-foreground">
                <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No cost data available yet</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== PRIME TAB (NEW) ==============
const PrimeTab = () => {
  const [activeSection, setActiveSection] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [memoryStats, setMemoryStats] = useState(null);
  const [memoryItems, setMemoryItems] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [models, setModels] = useState([]);
  const [trainingRules, setTrainingRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Memory Debug state
  const [memoryDebug, setMemoryDebug] = useState(null);
  const [testQuery, setTestQuery] = useState('');
  const [testingRetrieval, setTestingRetrieval] = useState(false);

  // Fetch all data
  useEffect(() => {
    fetchPrimeData();
  }, []);

  const fetchPrimeData = async () => {
    try {
      const [profileRes, statsRes, itemsRes, convosRes, modelsRes, rulesRes] = await Promise.all([
        axios.get(`${API}/prime/profile`),
        axios.get(`${API}/prime/memory/stats`),
        axios.get(`${API}/prime/memory/items?limit=50`),
        axios.get(`${API}/prime/conversations?limit=20`),
        axios.get(`${API}/prime/models`),
        axios.get(`${API}/prime/training/rules`)
      ]);
      setProfile(profileRes.data);
      setMemoryStats(statsRes.data);
      setMemoryItems(itemsRes.data.items || []);
      setConversations(convosRes.data.conversations || []);
      setModels(modelsRes.data.models || []);
      setTrainingRules(rulesRes.data.rules || []);
    } catch (error) {
      console.error('Error fetching Prime data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch memory debug info
  const fetchMemoryDebug = async () => {
    try {
      const res = await axios.get(`${API}/prime/memory/debug/last-retrieval`);
      setMemoryDebug(res.data);
    } catch (error) {
      console.error('Error fetching memory debug:', error);
    }
  };

  // Test memory retrieval
  const testMemoryRetrieval = async () => {
    if (!testQuery.trim()) return;
    setTestingRetrieval(true);
    try {
      const res = await axios.post(`${API}/prime/memory/debug/test-retrieval`, null, {
        params: { query: testQuery }
      });
      setMemoryDebug({
        has_data: true,
        timestamp: new Date().toISOString(),
        query: testQuery,
        total_retrieved: res.data.total_retrieved,
        tokens_used: res.data.tokens_estimate,
        items: res.data.debug_items || []
      });
      toast.success(`Retrieved ${res.data.total_retrieved} memories`);
    } catch (error) {
      toast.error('Failed to test retrieval');
    } finally {
      setTestingRetrieval(false);
    }
  };

  // Fetch debug when memory section is active
  useEffect(() => {
    if (activeSection === 'memory') {
      fetchMemoryDebug();
    }
  }, [activeSection]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/prime/profile/avatar`, formData);
      setProfile({ ...profile, avatar_url: res.data.avatar_url });
      toast.success('Avatar updated successfully!');
    } catch (error) {
      toast.error('Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleResetAvatar = async () => {
    try {
      await axios.post(`${API}/prime/profile/avatar/reset`);
      setProfile({ ...profile, avatar_url: null });
      toast.success('Avatar reset to default');
    } catch (error) {
      toast.error('Failed to reset avatar');
    }
  };

  const updateProfile = async (updates) => {
    setSavingProfile(true);
    try {
      await axios.patch(`${API}/prime/profile`, updates);
      setProfile({ ...profile, ...updates });
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const updateModelPolicy = async (provider, modelName, modelId) => {
    try {
      await axios.post(`${API}/prime/model-policies`, null, {
        params: {
          ai_system: 'prime',
          model_id: modelId,
          provider: provider,
          model_name: modelName
        }
      });
      setProfile({ ...profile, model_provider: provider, model_name: modelName });
      toast.success('Model updated for Prime');
    } catch (error) {
      toast.error('Failed to update model');
    }
  };

  const deleteMemoryItem = async (itemId) => {
    try {
      await axios.delete(`${API}/prime/memory/items/${itemId}`);
      setMemoryItems(memoryItems.filter(m => m.id !== itemId));
      toast.success('Memory item deleted');
    } catch (error) {
      toast.error('Failed to delete memory');
    }
  };

  const togglePinMemory = async (itemId, currentPinned) => {
    try {
      await axios.post(`${API}/prime/memory/items/${itemId}/pin`, null, {
        params: { pinned: !currentPinned }
      });
      setMemoryItems(memoryItems.map(m => m.id === itemId ? { ...m, is_pinned: !currentPinned } : m));
      toast.success(currentPinned ? 'Memory unpinned' : 'Memory pinned');
    } catch (error) {
      toast.error('Failed to update memory');
    }
  };

  const SECTIONS = [
    { id: 'profile', label: 'Prime Profile', icon: Crown },
    { id: 'memory', label: 'Prime Memory', icon: Brain },
    { id: 'calendar', label: 'Calendar Settings', icon: Calendar },
    { id: 'training', label: 'Prime Training', icon: GraduationCap },
    { id: 'model', label: 'Prime Model / LLM', icon: Cpu },
    { id: 'permissions', label: 'Permissions & Autonomy', icon: Shield },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {SECTIONS.map(section => {
          const Icon = section.icon;
          return (
            <Button
              key={section.id}
              variant={activeSection === section.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveSection(section.id)}
              className={`gap-2 whitespace-nowrap ${
                activeSection === section.id ? 'bg-amber-500 hover:bg-amber-600' : ''
              }`}
            >
              <Icon className="h-4 w-4" />
              {section.label}
            </Button>
          );
        })}
      </div>

      {/* Profile Section */}
      {activeSection === 'profile' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-amber-500" />
                Prime Profile
              </CardTitle>
              <CardDescription>Configure Prime's identity and appearance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Avatar Section */}
              <div className="flex items-start gap-6">
                <div className="relative">
                  <Avatar className="h-24 w-24 border-4 border-amber-200">
                    <AvatarImage src={profile?.avatar_url} />
                    <AvatarFallback className="bg-gradient-to-br from-amber-400 to-orange-500 text-white text-2xl">
                      <Crown className="h-10 w-10" />
                    </AvatarFallback>
                  </Avatar>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    id="avatar-upload"
                  />
                </div>
                <div className="space-y-3">
                  <div>
                    <h3 className="font-semibold text-lg">{profile?.name || 'Rhino-1 Prime'}</h3>
                    <p className="text-sm text-muted-foreground">{profile?.role || 'Super Admin AI Assistant'}</p>
                    <Badge variant={profile?.enabled ? 'default' : 'secondary'} className="mt-1">
                      {profile?.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={uploadingAvatar}
                      onClick={() => document.getElementById('avatar-upload').click()}
                    >
                      {uploadingAvatar ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                      Upload Image
                    </Button>
                    {profile?.avatar_url && (
                      <Button variant="ghost" size="sm" onClick={handleResetAvatar}>
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Reset Default
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Status Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Prime Status</Label>
                  <p className="text-sm text-muted-foreground">Enable or disable Prime assistant</p>
                </div>
                <Switch
                  checked={profile?.enabled}
                  onCheckedChange={(v) => updateProfile({ enabled: v })}
                />
              </div>

              {/* Identity Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Display Name</Label>
                  <Input
                    value={profile?.name || ''}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    onBlur={() => updateProfile({ name: profile?.name })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Role/Title</Label>
                  <Input
                    value={profile?.role || ''}
                    onChange={(e) => setProfile({ ...profile, role: e.target.value })}
                    onBlur={() => updateProfile({ role: profile?.role })}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Avatar History */}
              {profile?.avatar_history?.length > 0 && (
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <History className="h-4 w-4" />
                    Avatar History
                  </Label>
                  <div className="flex gap-2">
                    {profile.avatar_history.slice(-5).map((item, i) => (
                      <Avatar key={i} className="h-10 w-10 cursor-pointer hover:ring-2 ring-amber-500"
                        onClick={() => updateProfile({ avatar_url: item.url })}
                      >
                        <AvatarImage src={item.url} />
                      </Avatar>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Memory Section */}
      {activeSection === 'memory' && (
        <div className="space-y-6">
          {/* Memory Stats */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{memoryStats?.total_items || 0}</p>
                <p className="text-xs text-muted-foreground">Memory Items</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{memoryStats?.total_conversations || 0}</p>
                <p className="text-xs text-muted-foreground">Conversations</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{memoryStats?.total_summaries || 0}</p>
                <p className="text-xs text-muted-foreground">Summaries</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{memoryStats?.pinned_count || 0}</p>
                <p className="text-xs text-muted-foreground">Pinned</p>
              </CardContent>
            </Card>
          </div>

          {/* Memory Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-purple-500" />
                Memory Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Enable Persistent Memory</Label>
                  <p className="text-sm text-muted-foreground">Prime will remember across chats</p>
                </div>
                <Switch
                  checked={profile?.memory_enabled}
                  onCheckedChange={(v) => updateProfile({ memory_enabled: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Auto-Extract Memories</Label>
                  <p className="text-sm text-muted-foreground">Automatically extract facts from conversations</p>
                </div>
                <Switch
                  checked={profile?.auto_extract_memories}
                  onCheckedChange={(v) => updateProfile({ auto_extract_memories: v })}
                />
              </div>
              <div>
                <Label className="font-medium">Memory Retention</Label>
                <Select
                  value={String(profile?.memory_retention_days || 365)}
                  onValueChange={(v) => updateProfile({ memory_retention_days: parseInt(v) })}
                >
                  <SelectTrigger className="w-48 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="180">6 months</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                    <SelectItem value="730">2 years</SelectItem>
                    <SelectItem value="0">Forever</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Memory Items */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Memory Items</CardTitle>
                <Button variant="outline" size="sm" onClick={() => {/* TODO: Add memory modal */}}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Memory
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {memoryItems.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Brain className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No memory items yet</p>
                    <p className="text-xs">Start chatting with Prime to build memory</p>
                  </div>
                ) : (
                  memoryItems.map(item => (
                    <div key={item.id} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
                      <div className="flex-1">
                        <p className="text-sm">{item.content}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">{item.item_type}</Badge>
                          <Badge variant="secondary" className="text-xs">{item.scope}</Badge>
                          {item.is_pinned && <Pin className="h-3 w-3 text-amber-500" />}
                          <span className="text-xs text-muted-foreground">
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => togglePinMemory(item.id, item.is_pinned)}>
                          <Pin className={`h-4 w-4 ${item.is_pinned ? 'text-amber-500' : ''}`} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteMemoryItem(item.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Memory Debug Panel */}
          <Card className="border-purple-200 dark:border-purple-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bug className="h-5 w-5 text-purple-500" />
                Memory Retrieval Debug
              </CardTitle>
              <CardDescription>
                See what memories Prime retrieves and why for each response
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Test Retrieval */}
              <div className="flex gap-2">
                <Input
                  placeholder="Test a query to see what memories would be retrieved..."
                  value={testQuery}
                  onChange={(e) => setTestQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && testMemoryRetrieval()}
                />
                <Button 
                  onClick={testMemoryRetrieval} 
                  disabled={testingRetrieval || !testQuery.trim()}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {testingRetrieval ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-1" />
                      Test
                    </>
                  )}
                </Button>
              </div>

              {/* Last Retrieval Info */}
              {memoryDebug?.has_data ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      Last query: <span className="font-medium text-foreground">&ldquo;{memoryDebug.query?.slice(0, 50)}...&rdquo;</span>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {memoryDebug.timestamp && new Date(memoryDebug.timestamp).toLocaleString()}
                    </span>
                  </div>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded text-center">
                      <p className="text-lg font-bold text-purple-600">{memoryDebug.total_retrieved || 0}</p>
                      <p className="text-xs text-muted-foreground">Items Retrieved</p>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded text-center">
                      <p className="text-lg font-bold text-blue-600">{memoryDebug.tokens_used || 0}</p>
                      <p className="text-xs text-muted-foreground">Tokens Used</p>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded text-center">
                      <p className="text-lg font-bold text-green-600">
                        {memoryDebug.retrieval_stages?.reduce((sum, s) => sum + (s.count || 0), 0) || memoryDebug.items?.length || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Sources</p>
                    </div>
                  </div>

                  {/* Retrieved Items */}
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {memoryDebug.items?.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-2 border rounded-lg text-sm bg-slate-50 dark:bg-slate-900">
                        <div className="flex-1">
                          <p className="text-xs">{item.preview || item.content?.slice(0, 100)}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                item.selection_reason === 'pinned' ? 'border-amber-500 text-amber-600' :
                                item.selection_reason === 'open_loop' ? 'border-orange-500 text-orange-600' :
                                item.selection_reason === 'keyword_match' ? 'border-blue-500 text-blue-600' :
                                'border-gray-500'
                              }`}
                            >
                              {item.selection_reason}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              Score: {item.score?.toFixed(1) || 'N/A'}
                            </span>
                            {item.injected && (
                              <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                                Injected
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Bug className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>No retrieval data yet</p>
                  <p className="text-xs">Send a message to Prime or test a query above</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Conversation History */}
          <Card>
            <CardHeader>
              <CardTitle>Conversation History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {conversations.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <MessageCircle className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>No conversations yet</p>
                  </div>
                ) : (
                  conversations.map(convo => (
                    <div key={convo.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{convo.summary || 'Conversation'}</p>
                        <p className="text-xs text-muted-foreground">
                          {convo.message_count} messages • {new Date(convo.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Badge variant="outline">{convo.key_topics?.slice(0, 2).join(', ') || 'General'}</Badge>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Purge Actions */}
          <Card className="border-red-200 dark:border-red-800">
            <CardHeader>
              <CardTitle className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                These actions are irreversible. Pinned memories will not be deleted.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" className="text-red-600 border-red-300">
                  Purge Old Memories
                </Button>
                <Button variant="destructive">
                  Purge All Memory
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Training Section */}
      {activeSection === 'training' && (
        <div className="space-y-6">
          {/* Knowledge Training */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-blue-500" />
                Knowledge Training
              </CardTitle>
              <CardDescription>Link Knowledge Base collections to Prime</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Attached KB Collections</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(profile?.kb_collections || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No collections linked</p>
                  ) : (
                    profile.kb_collections.map(col => (
                      <Badge key={col} variant="secondary">{col}</Badge>
                    ))
                  )}
                  <Button variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    Link Collection
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Behavior Training */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sliders className="h-5 w-5 text-purple-500" />
                Behavior Training
              </CardTitle>
              <CardDescription>Configure Prime's personality and decision-making</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Communication Tone</Label>
                  <Select
                    value={profile?.tone || 'executive'}
                    onValueChange={(v) => updateProfile({ tone: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="executive">Executive</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="direct">Direct</SelectItem>
                      <SelectItem value="friendly">Friendly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Risk Tolerance</Label>
                  <Select
                    value={profile?.risk_tolerance || 'moderate'}
                    onValueChange={(v) => updateProfile({ risk_tolerance: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conservative">Conservative</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="aggressive">Aggressive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Clarification Threshold</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  How confident Prime should be before acting vs asking
                </p>
                <div className="flex items-center gap-4">
                  <Input
                    type="range"
                    min="0"
                    max="100"
                    value={(profile?.clarification_threshold || 0.7) * 100}
                    onChange={(e) => updateProfile({ clarification_threshold: parseInt(e.target.value) / 100 })}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium w-12">{Math.round((profile?.clarification_threshold || 0.7) * 100)}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Training Rules */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Custom Training Rules</CardTitle>
                  <CardDescription>Add specific rules for Prime to follow</CardDescription>
                </div>
                <Button variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Rule
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {trainingRules.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>No custom rules defined</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {trainingRules.map(rule => (
                    <div key={rule.id} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{rule.name}</span>
                        <Badge variant="outline">{rule.rule_type}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{rule.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feedback Loop */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ThumbsUp className="h-5 w-5 text-green-500" />
                Feedback Training Loop
              </CardTitle>
              <CardDescription>Improve Prime based on response feedback</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <ThumbsUp className="h-8 w-8 mx-auto text-green-500 mb-2" />
                  <p className="text-2xl font-bold">0</p>
                  <p className="text-xs text-muted-foreground">Positive</p>
                </div>
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                  <ThumbsDown className="h-8 w-8 mx-auto text-red-500 mb-2" />
                  <p className="text-2xl font-bold">0</p>
                  <p className="text-xs text-muted-foreground">Negative</p>
                </div>
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                  <Flag className="h-8 w-8 mx-auto text-amber-500 mb-2" />
                  <p className="text-2xl font-bold">0</p>
                  <p className="text-xs text-muted-foreground">Flagged</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Feedback from Prime responses will appear here for review and training improvements.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Model / LLM Section */}
      {activeSection === 'model' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cpu className="h-5 w-5 text-blue-500" />
                Prime Model / LLM
              </CardTitle>
              <CardDescription>Select the AI model powering Prime</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm font-medium">Current Model</p>
                <p className="text-2xl font-bold text-blue-600">
                  {profile?.model_name || 'gpt-4o'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Provider: {profile?.model_provider || 'openai'}
                </p>
              </div>

              <div>
                <Label>Select Model</Label>
                <div className="grid gap-3 mt-2">
                  {models.map(model => (
                    <div
                      key={model.id}
                      onClick={() => updateModelPolicy(model.provider, model.model_name, model.id)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        profile?.model_name === model.model_name
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{model.display_name}</p>
                          <p className="text-xs text-muted-foreground">{model.description}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant={model.cost_tier === 'premium' ? 'default' : 'secondary'}>
                            {model.cost_tier}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {model.context_window.toLocaleString()} tokens
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        {model.capabilities?.vision && <Badge variant="outline" className="text-xs">Vision</Badge>}
                        {model.capabilities?.function_calling && <Badge variant="outline" className="text-xs">Functions</Badge>}
                        {model.capabilities?.large_context && <Badge variant="outline" className="text-xs">Large Context</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Test Prompt */}
              <Card className="bg-slate-50 dark:bg-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <TestTube className="h-4 w-4" />
                    Test Prompt
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea placeholder="Enter a test prompt to verify model..." className="mb-2" />
                  <Button size="sm">
                    <Play className="h-4 w-4 mr-1" />
                    Test
                  </Button>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Calendar Settings Section */}
      {activeSection === 'calendar' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                Calendar Tool Settings
              </CardTitle>
              <CardDescription>Configure how Prime creates calendar events</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Default Calendar Target */}
              <div className="space-y-2">
                <Label className="font-medium">Default Calendar Target</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  When Prime creates events, which calendar should they be added to by default?
                </p>
                <Select
                  value={profile?.default_calendar_target || 'super_admin'}
                  onValueChange={(v) => updateProfile({ default_calendar_target: v })}
                >
                  <SelectTrigger className="w-full max-w-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin Calendar (Default)</SelectItem>
                    <SelectItem value="team">Team Calendar</SelectItem>
                    <SelectItem value="ask">Ask Each Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Auto-Add Reminders */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="font-medium">Auto-Add Reminders</Label>
                  <p className="text-sm text-muted-foreground">Automatically add reminders to Prime-created events</p>
                </div>
                <Switch
                  checked={profile?.calendar_auto_reminders ?? true}
                  onCheckedChange={(v) => updateProfile({ calendar_auto_reminders: v })}
                />
              </div>

              {/* Default Reminder Time */}
              {(profile?.calendar_auto_reminders ?? true) && (
                <div className="space-y-2">
                  <Label className="font-medium">Default Reminder Time</Label>
                  <Select
                    value={String(profile?.calendar_reminder_minutes || 30)}
                    onValueChange={(v) => updateProfile({ calendar_reminder_minutes: parseInt(v) })}
                  >
                    <SelectTrigger className="w-full max-w-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 minutes before</SelectItem>
                      <SelectItem value="10">10 minutes before</SelectItem>
                      <SelectItem value="15">15 minutes before</SelectItem>
                      <SelectItem value="30">30 minutes before</SelectItem>
                      <SelectItem value="60">1 hour before</SelectItem>
                      <SelectItem value="1440">1 day before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Separator />

              {/* Action Truth Policy Notice */}
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">Action Truth Policy</p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      Prime will <strong>never</strong> claim to create a calendar event without actually creating it.
                      Every event creation is executed through the backend tool and confirmed with a success/failure card.
                    </p>
                  </div>
                </div>
              </div>

              {/* AI-Created Events Filter */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="font-medium">Show AI-Created Events in Calendar</Label>
                  <p className="text-sm text-muted-foreground">Display events created by Prime in the main calendar view</p>
                </div>
                <Switch
                  checked={profile?.show_ai_events_in_calendar ?? true}
                  onCheckedChange={(v) => updateProfile({ show_ai_events_in_calendar: v })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Recent Prime Calendar Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Recent Calendar Actions by Prime
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground text-center py-4">
                Recent calendar events created by Prime will appear here for audit and review.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Permissions & Autonomy Section */}
      {activeSection === 'permissions' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-green-500" />
                Autonomy Level
              </CardTitle>
              <CardDescription>Configure Prime's decision-making authority</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Current Autonomy Level</p>
                    <p className="text-3xl font-bold text-amber-600">Level {profile?.autonomy_level || 2}</p>
                    <p className="text-xs text-muted-foreground">Bounded Autonomy</p>
                  </div>
                  <Select
                    value={String(profile?.autonomy_level || 2)}
                    onValueChange={(v) => updateProfile({ autonomy_level: parseInt(v) })}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Level 1 - Minimal</SelectItem>
                      <SelectItem value="2">Level 2 - Bounded</SelectItem>
                      <SelectItem value="3">Level 3 - Guided</SelectItem>
                      <SelectItem value="4">Level 4 - Autonomous</SelectItem>
                      <SelectItem value="5">Level 5 - Full</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="font-medium">Auto-Execute Safe Actions</Label>
                  <p className="text-sm text-muted-foreground">Execute read-only and low-risk actions automatically</p>
                </div>
                <Switch
                  checked={profile?.auto_execute_safe_actions}
                  onCheckedChange={(v) => updateProfile({ auto_execute_safe_actions: v })}
                />
              </div>

              <div>
                <Label className="font-medium mb-2 block">Actions Requiring Approval</Label>
                <div className="flex flex-wrap gap-2">
                  {['send_sms', 'send_email', 'update_job_status', 'financial_actions', 'schedule_changes', 'create_job', 'delete_data'].map(action => {
                    const isRequired = (profile?.require_approval_for || []).includes(action);
                    return (
                      <Badge
                        key={action}
                        variant={isRequired ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => {
                          const current = profile?.require_approval_for || [];
                          const updated = isRequired
                            ? current.filter(a => a !== action)
                            : [...current, action];
                          updateProfile({ require_approval_for: updated });
                        }}
                      >
                        {isRequired && <Lock className="h-3 w-3 mr-1" />}
                        {action.replace(/_/g, ' ')}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Autonomy Level Guide */}
          <Card>
            <CardHeader>
              <CardTitle>Autonomy Level Guide</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { level: 1, name: 'Minimal', desc: 'Prime can only answer questions and provide information', color: 'bg-slate-100' },
                  { level: 2, name: 'Bounded', desc: 'Can create tasks, draft messages, and make suggestions. Cannot execute actions.', color: 'bg-blue-100' },
                  { level: 3, name: 'Guided', desc: 'Can execute approved actions with human confirmation for important decisions.', color: 'bg-green-100' },
                  { level: 4, name: 'Autonomous', desc: 'Can execute most actions independently with post-action reporting.', color: 'bg-amber-100' },
                  { level: 5, name: 'Full', desc: 'Complete autonomy to execute any action within defined guardrails.', color: 'bg-red-100' },
                ].map(item => (
                  <div
                    key={item.level}
                    className={`p-3 rounded-lg ${item.color} dark:bg-opacity-20 ${
                      profile?.autonomy_level === item.level ? 'ring-2 ring-amber-500' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-bold">Level {item.level}</span>
                      <span className="font-medium">- {item.name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============== SETTINGS TAB ==============
const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform-ai/settings`);
      setSettings(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSetting = async (key, value) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/platform-ai/settings`, { [key]: value });
      setSettings({ ...settings, [key]: value });
      toast.success('Setting updated');
    } catch (error) {
      toast.error('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Platform AI Settings</h3>
        <p className="text-sm text-muted-foreground">
          Global configuration for all AI systems
        </p>
      </div>

      {/* Global Toggles */}
      <Card>
        <CardHeader>
          <CardTitle>System Controls</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Brain className="h-5 w-5 text-blue-500" />
              <div>
                <p className="font-medium">AI Platform Master Switch</p>
                <p className="text-sm text-muted-foreground">
                  Enable or disable all AI functionality
                </p>
              </div>
            </div>
            <Switch
              checked={settings?.ai_enabled}
              onCheckedChange={(v) => updateSetting('ai_enabled', v)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">AI Assistant</p>
              </div>
              <Switch
                checked={settings?.assistant_enabled}
                onCheckedChange={(v) => updateSetting('assistant_enabled', v)}
              />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Chatbots</p>
              </div>
              <Switch
                checked={settings?.chatbots_enabled}
                onCheckedChange={(v) => updateSetting('chatbots_enabled', v)}
              />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">AI Agents</p>
              </div>
              <Switch
                checked={settings?.agents_enabled}
                onCheckedChange={(v) => updateSetting('agents_enabled', v)}
              />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">AI Trainer</p>
              </div>
              <Switch
                checked={settings?.trainer_enabled}
                onCheckedChange={(v) => updateSetting('trainer_enabled', v)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Model Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Model Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Default Provider</Label>
              <Select defaultValue={settings?.default_provider || 'openai'}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="openai">OpenAI</SelectItem>
                  <SelectItem value="anthropic">Anthropic</SelectItem>
                  <SelectItem value="gemini">Google Gemini</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Default Model</Label>
              <Select defaultValue={settings?.default_model || 'gpt-4o'}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                  <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                  <SelectItem value="gpt-5.1">GPT-5.1</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cost Limits */}
      <Card>
        <CardHeader>
          <CardTitle>Cost Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Daily Cost Limit</Label>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-muted-foreground">$</span>
              <Input
                type="number"
                defaultValue={(settings?.daily_cost_limit_cents || 10000) / 100}
                className="w-32"
              />
              <span className="text-muted-foreground">per day</span>
            </div>
          </div>
          <div>
            <Label>Alert Threshold</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                defaultValue={settings?.alert_threshold_percent || 80}
                className="w-24"
              />
              <span className="text-muted-foreground">% of daily limit</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== MAIN COMPONENT ==============
const PlatformAI = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard';

  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header - Sticky with solid background */}
        <div className="sticky top-0 z-10 bg-background pb-4 -mx-6 px-6 pt-0 border-b">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl">
                <Brain className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Platform AI</h1>
                <p className="text-muted-foreground">
                  AI Control Center for Rhino-1 Enterprise
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="gap-1">
                <Sparkles className="h-3 w-3" />
                Powered by GPT-4o
              </Badge>
            </div>
          </div>
        </div>

        {/* Tabs with horizontal scroll and blue scrollbar */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="relative">
            <div 
              className="overflow-x-auto overflow-y-hidden pb-2"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#3b82f6 #e2e8f0'
              }}
            >
              <style>{`
                .platform-ai-tabs::-webkit-scrollbar {
                  height: 6px;
                }
                .platform-ai-tabs::-webkit-scrollbar-track {
                  background: #e2e8f0;
                  border-radius: 3px;
                }
                .platform-ai-tabs::-webkit-scrollbar-thumb {
                  background: #3b82f6;
                  border-radius: 3px;
                }
                .platform-ai-tabs::-webkit-scrollbar-thumb:hover {
                  background: #2563eb;
                }
              `}</style>
              <TabsList className="platform-ai-tabs inline-flex w-max min-w-full justify-start h-12 gap-1 bg-slate-100 dark:bg-slate-800 p-1">
                {TABS.map(tab => {
                  const Icon = tab.icon;
                  const isPrime = tab.id === 'prime';
                  return (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className={`data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 gap-2 whitespace-nowrap ${
                        isPrime ? 'data-[state=active]:text-amber-600' : ''
                      }`}
                    >
                      <Icon className={`h-4 w-4 ${isPrime ? 'text-amber-500' : ''}`} />
                      <span>{tab.label}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
          </div>

          <div className="mt-6">
            <TabsContent value="dashboard"><DashboardTab /></TabsContent>
            <TabsContent value="prime"><PrimeTab /></TabsContent>
            <TabsContent value="assistant"><AssistantTab /></TabsContent>
            <TabsContent value="chatbots"><ChatbotsTab /></TabsContent>
            <TabsContent value="agents"><AgentsTab /></TabsContent>
            <TabsContent value="trainer"><TrainerTab /></TabsContent>
            <TabsContent value="knowledge"><KnowledgeBaseTab /></TabsContent>
            <TabsContent value="tools"><ToolsTab /></TabsContent>
            <TabsContent value="governance"><GovernanceTab /></TabsContent>
            <TabsContent value="analytics"><AnalyticsTab /></TabsContent>
            <TabsContent value="settings"><SettingsTab /></TabsContent>
          </div>
        </Tabs>
      </div>
    </Layout>
  );
};

export default PlatformAI;
